import { map, mergeMap } from "rxjs/operators";

import { Handler } from "@tsp/api";
import { parseServerError } from "@tsp/api/utils/parseServerError";
import { Epic, combineEpics, ofAction } from "@tsp/utils/epics";
import { catchAsyncError } from "@tsp/utils/epics/catchAsyncError";

import { showNotification } from "@tsp-core/notifier/slice";
import { checkIsNotNullish } from "@tsp-core/utils/nullish";

import { openCommunicationLinkAction, openExternalResourceAttachmentAction } from "../actions";
import {
  GetAttachmentLinkRequestParams,
  GetAttachmentLinkResponseResult,
  GetCommunicationLinkRequestParams,
  GetCommunicationLinkResponseResult,
} from "../api";

export type ExternalResourcesEpicDependencies = {
  api: {
    getAttachmentLink: Handler<GetAttachmentLinkRequestParams, GetAttachmentLinkResponseResult>;
    getCommunicationLink: Handler<GetCommunicationLinkRequestParams, GetCommunicationLinkResponseResult>;
  };
};

const onOpenExternalResourceAttachmentEpic: Epic<unknown, ExternalResourcesEpicDependencies> = (actions$, _, deps) => {
  return actions$.pipe(
    ofAction(openExternalResourceAttachmentAction.started),
    mergeMap((action) => {
      return deps.api.getAttachmentLink(action.payload).pipe(
        map((response) => {
          window.open(response.temporaryLink);

          return openExternalResourceAttachmentAction.done({
            params: action.payload,
            result: null,
          });
        }),
        catchAsyncError((error) => {
          return openExternalResourceAttachmentAction.failed({
            params: action.payload,
            error: parseServerError(error),
          });
        }),
      );
    }),
  );
};

const onOpenExternalResourceCommunicationLinkAction: Epic<unknown, ExternalResourcesEpicDependencies> = (
  actions$,
  _,
  deps,
) => {
  return actions$.pipe(
    ofAction(openCommunicationLinkAction.started),
    mergeMap((action) =>
      deps.api.getCommunicationLink(action.payload).pipe(
        map((response) => {
          window.open(response.permalink);
          action.payload.onSuccess?.();

          return openCommunicationLinkAction.done({
            params: action.payload,
            result: null,
          });
        }),
        catchAsyncError((error) => {
          return openCommunicationLinkAction.failed({
            params: action.payload,
            error: parseServerError(error),
          });
        }),
      ),
    ),
  );
};

const onFailureEpic: Epic<unknown, ExternalResourcesEpicDependencies> = (actions$) => {
  return actions$.pipe(
    ofAction(openExternalResourceAttachmentAction.failed, openCommunicationLinkAction.failed),
    mergeMap((action) => {
      const actions = [];

      if (typeof action.payload.error === "string") {
        actions.push(
          showNotification({
            type: "error",
            message: action.payload.error,
          }),
        );
      }

      if (typeof action.payload.error === "object" && checkIsNotNullish(action.payload.error)) {
        Object.values(action.payload.error).forEach((error) => {
          if (typeof error === "string") {
            actions.push(
              showNotification({
                type: "error",
                message: error,
              }),
            );
          }
        });
      }

      return actions;
    }),
  );
};

export const externalResourcesEpic = combineEpics(
  onOpenExternalResourceAttachmentEpic,
  onOpenExternalResourceCommunicationLinkAction,
  onFailureEpic,
);
